// Shared email validation, used by the signup/login modals (client) and the
// check-email route (server) so all three agree on what "valid" means. The
// old flow duplicated a loose /^[^\s@]+@[^\s@]+\.[^\s@]+$/ in three places,
// which let obvious junk through (e.g. "test@gmial.cob") — see the QA report
// on missing sign-up email validation.
//
// Two layers, deliberately split:
//   1. isValidEmailStructure — shape only. Safe to run anywhere (no deps),
//      so the modals call it for instant inline feedback.
//   2. hasRealTld — confirms the top-level domain actually exists, via the
//      `psl` public-suffix list. This is the ONLY thing that catches a
//      typo'd-but-well-shaped TLD like ".cob". It lives in a server-only
//      module (email-domain.ts) so psl's suffix list never ships to the
//      browser bundle.

// RFC-5322-shaped pattern, matching the stricter one already used by the
// growth contact verifier (lib/growth/verify-contacts.ts). Stricter than the
// old check: it rejects 1-char TLDs, double dots, and domain labels that
// start/end with a hyphen, while still accepting plus-addressing, subdomains,
// and multi-part TLDs (e.g. user+tag@sub.example.co.uk).
//
// It only validates SHAPE — it cannot tell a real TLD from a typo'd one. Use
// hasRealTld (server) for that.
export const EMAIL_STRUCTURE_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

// 254 is the RFC 5321 maximum length of a full email address.
const MAX_EMAIL_LENGTH = 254;

/**
 * Structural email check — shape only, no network or suffix-list lookup.
 * Trims the input itself so callers can pass the raw field value.
 */
export function isValidEmailStructure(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed || trimmed.length > MAX_EMAIL_LENGTH) return false;
  return EMAIL_STRUCTURE_RE.test(trimmed);
}
